import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import ParentCourseForm from './ParentCourseForm';
import CourseManager from './CourseManager';
import CourseBookingsOverview from './CourseBookingsOverview';
import MemberList from './MemberList';
import MemberForm from './MemberForm';
import MembershipApplications from './MembershipApplications';
import VisitSlotManager from './VisitSlotManager';
import WorkshopsManager from './WorkshopsManager';
import FeriencampsManager from './FeriencampsManager';
import GeburtstagManagerV2 from './GeburtstagManagerV2';
import SchnupperTerminManager from './SchnupperTerminManager';
import SchnitzeljagdManager from './SchnitzeljagdManager';
import InternetfuehrerscheinManager from './InternetfuehrerscheinManager';
import RechnungenManager from './RechnungenManager';
import BackupManager from './BackupManager';
import InvoiceSettings from './InvoiceSettings';
import AdminUsersManager from './AdminUsersManager';
import NewsletterAdmin from './NewsletterAdmin';
import BuchhaltungManager from './BuchhaltungManager';
import { useAuth } from './AuthContext';

// Mitglieder-Uebersicht kapselt den Bearbeiten-Modus (Liste <-> Formular),
// damit dieser Wechsel keine eigene Route braucht.
function MitgliederUebersicht() {
  const [editingMember, setEditingMember] = useState(null);
  const location = useLocation();
  const { authFetch } = useAuth();

  useEffect(() => {
    const editMemberId = location.state?.editMemberId;
    if (!editMemberId) return;
    authFetch(`/api/members/${editMemberId}`)
      .then(res => res.json())
      .then(data => {
        if (data.data) setEditingMember(data.data);
      })
      .catch(err => console.error('Error fetching new member:', err));
  }, [location.state]);

  if (editingMember) {
    return (
      <MemberForm
        member={editingMember}
        onSave={() => setEditingMember(null)}
        onCancel={() => setEditingMember(null)}
      />
    );
  }

  return <MemberList onEdit={setEditingMember} />;
}

function MitgliederAntraege() {
  const navigate = useNavigate();
  return (
    <MembershipApplications
      onMemberCreated={(memberId) => navigate('/admin/mitglieder/uebersicht', { state: { editMemberId: memberId } })}
    />
  );
}

function NeuesMitglied() {
  const navigate = useNavigate();
  const zurueckZurUebersicht = () => navigate('/admin/mitglieder/uebersicht');
  return <MemberForm onSave={zurueckZurUebersicht} onCancel={zurueckZurUebersicht} />;
}

function AdminOverview({ sections }) {
  return (
    <div>
      <h2 className="mb-4">Admin Bereich</h2>
      <div className="row g-3">
        {sections.map(section => (
          <div className="col-md-4" key={section.id}>
            <Link
              to={`/admin/${section.id}/${section.items[0].id}`}
              className="card h-100 text-decoration-none text-dark shadow-sm"
            >
              <div className="card-body">
                <h5 className="card-title">{section.label}</h5>
                <p className="card-text text-muted small mb-0">
                  {section.items.map(item => item.label).join(' · ')}
                </p>
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminSection({ section }) {
  return (
    <div>
      <Link to="/admin" className="text-muted small d-inline-block mb-1">&larr; Admin-Uebersicht</Link>
      <h3 className="mb-3">{section.label}</h3>

      {section.items.length > 1 && (
        <ul className="nav nav-pills mb-4">
          {section.items.map(item => (
            <li className="nav-item" key={item.id}>
              <NavLink to={`/admin/${section.id}/${item.id}`} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}

      <Routes>
        {section.items.map(item => (
          <Route key={item.id} path={item.id} element={item.element} />
        ))}
        <Route index element={<Navigate to={`/admin/${section.id}/${section.items[0].id}`} replace />} />
      </Routes>
    </div>
  );
}

function Admin() {
  const ADMIN_SECTIONS = [
    {
      id: 'kurse',
      label: 'Kurse',
      items: [
        { id: 'manage', label: 'Kurse verwalten', element: <CourseManager /> },
        { id: 'anmeldungen', label: 'Anmeldungen', element: <CourseBookingsOverview /> },
        { id: 'new-parent', label: 'Neuen Kurs erstellen', element: <ParentCourseForm /> }
      ]
    },
    {
      id: 'mitglieder',
      label: 'Mitglieder',
      items: [
        { id: 'uebersicht', label: 'Uebersicht', element: <MitgliederUebersicht /> },
        { id: 'neu', label: 'Neues Mitglied', element: <NeuesMitglied /> },
        { id: 'antraege', label: 'Antraege', element: <MitgliederAntraege /> }
      ]
    },
    {
      id: 'ferienworkshops',
      label: 'Ferienworkshops',
      items: [
        { id: 'feriencamps', label: 'Feriencamps', element: <FeriencampsManager /> },
        { id: 'schnitzeljagd', label: 'Schnitzeljagd', element: <SchnitzeljagdManager /> }
      ]
    },
    {
      id: 'geburtstage',
      label: 'Geburtstage',
      items: [
        { id: 'uebersicht', label: 'Uebersicht', element: <GeburtstagManagerV2 /> }
      ]
    },
    {
      id: 'internetfuehrerschein',
      label: 'Internetfuehrerschein',
      items: [
        { id: 'uebersicht', label: 'Uebersicht', element: <InternetfuehrerscheinManager /> }
      ]
    },
    {
      id: 'newsletter',
      label: 'Newsletter & Events',
      items: [
        { id: 'events', label: 'Workshops & Events', element: <WorkshopsManager /> },
        { id: 'verwalten', label: 'Newsletter verwalten', element: <NewsletterAdmin /> }
      ]
    },
    {
      id: 'buchhaltung',
      label: 'Buchhaltung',
      items: [
        { id: 'uebersicht', label: 'Uebersicht', element: <BuchhaltungManager /> },
        { id: 'rechnungen', label: 'Rechnungen', element: <RechnungenManager /> },
        { id: 'einstellungen', label: 'Rechnungseinstellungen', element: <InvoiceSettings /> }
      ]
    },
    {
      id: 'kennenlernen',
      label: 'Kennenlernen',
      items: [
        { id: 'besuche', label: 'Besuchstermine', element: <VisitSlotManager /> },
        { id: 'schnuppern', label: 'Schnuppern', element: <SchnupperTerminManager /> }
      ]
    },
    {
      id: 'einstellungen',
      label: 'Einstellungen',
      items: [
        { id: 'admins', label: 'Admin-Benutzer', element: <AdminUsersManager /> },
        { id: 'backup', label: 'Backup & Wiederherstellung', element: <BackupManager /> }
      ]
    }
  ];

  return (
    <Routes>
      <Route index element={<AdminOverview sections={ADMIN_SECTIONS} />} />
      {ADMIN_SECTIONS.map(section => (
        <Route key={section.id} path={`${section.id}/*`} element={<AdminSection section={section} />} />
      ))}
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

export default Admin;
